import { createBrowserRouter, type RouteObject } from "react-router";
import { MainLayout } from "./components/MainLayout";
import { RootLayout } from "./components/RootLayout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { NotFoundPage } from "./components/NotFoundPage";
import { LoginPage } from "./components/LoginPage";
import { SignupPage } from "./components/SignupPage";

// Layout ve auth sayfaları eager — kullanıcı ilk bunları görür
// Tüm diğer sayfalar route-level lazy — ilk ziyarette indirilir

export const router = createBrowserRouter([
  {
    path: "/",
    Component: RootLayout,
    ErrorBoundary,
    children: [
      // Public routes
      {
        path: "carwash/book/:tenantId",
        lazy: async () => {
          const { CarWashBookingPage } = await import("./components/CarWashBookingPage");
          return { Component: CarWashBookingPage };
        },
      },
      {
        path: "barber/book/:tenantId",
        lazy: async () => {
          const { BarberBookingPage } = await import("./components/BarberBookingPage");
          return { Component: BarberBookingPage };
        },
      },
      {
        path: "sofawash/book/:tenantId",
        lazy: async () => {
          const { SofaWashBookingPage } = await import("./components/SofaWashBookingPage");
          return { Component: SofaWashBookingPage };
        },
      },
      { path: "login", Component: LoginPage },
      {
        path: "mk/:code",
        lazy: async () => {
          const { CustomerQrPage } = await import("./components/CustomerQrPage");
          return { Component: CustomerQrPage };
        },
      },
      {
        path: "fis-pdf/:code",
        lazy: async () => {
          const { ReceiptPdfPage } = await import("./components/ReceiptPdfPage");
          return { Component: ReceiptPdfPage };
        },
      },
      {
        path: "fis/:code",
        lazy: async () => {
          const { ReceiptBarcodePage } = await import("./components/ReceiptBarcodePage");
          return { Component: ReceiptBarcodePage };
        },
      },
      { path: "signup", Component: SignupPage },
      {
        path: "payment/subscription-callback",
        lazy: async () => {
          const { SubscriptionCallbackPage } = await import("./components/payment/SubscriptionCallbackPage");
          return { Component: SubscriptionCallbackPage };
        },
      },
      {
        path: "payment/paytr/callback",
        lazy: async () => {
          const { PaymentCallbackPage } = await import("./components/payment/PaymentCallbackPage");
          return { Component: PaymentCallbackPage };
        },
      },
      {
        path: "davet/:code",
        lazy: async () => {
          const { InviteRedeemPage } = await import("./components/InviteRedeemPage");
          return { Component: InviteRedeemPage };
        },
      },

      // Admin routes
      {
        path: "admin/login",
        lazy: async () => {
          const { AdminLoginPage } = await import("./components/admin/AdminLoginPage");
          return { Component: AdminLoginPage };
        },
      },
      {
        path: "admin",
        lazy: async () => {
          const { AdminDashboardPage } = await import("./components/admin/AdminDashboardPage");
          return { Component: AdminDashboardPage };
        },
      },
      {
        path: "admin/dashboard",
        lazy: async () => {
          const { AdminDashboardPage } = await import("./components/admin/AdminDashboardPage");
          return { Component: AdminDashboardPage };
        },
      },
      {
        path: "admin/tenants",
        lazy: async () => {
          const { AdminTenantsPage } = await import("./components/admin/AdminTenantsPage");
          return { Component: AdminTenantsPage };
        },
      },
      {
        path: "admin/tenants/new",
        lazy: async () => {
          const { AdminTenantDetailPage } = await import("./components/admin/AdminTenantDetailPage");
          return { Component: AdminTenantDetailPage };
        },
      },
      {
        path: "admin/tenants/:id",
        lazy: async () => {
          const { AdminTenantDetailPage } = await import("./components/admin/AdminTenantDetailPage");
          return { Component: AdminTenantDetailPage };
        },
      },
      {
        path: "admin/invoices",
        lazy: async () => {
          const { AdminInvoicesPage } = await import("./components/admin/AdminInvoicesPage");
          return { Component: AdminInvoicesPage };
        },
      },
      {
        path: "admin/invoices/pending",
        lazy: async () => {
          const { AdminInvoicesPage } = await import("./components/admin/AdminInvoicesPage");
          return { Component: AdminInvoicesPage };
        },
      },
      {
        path: "admin/invoices/new",
        lazy: async () => {
          const { AdminInvoicesPage } = await import("./components/admin/AdminInvoicesPage");
          return { Component: AdminInvoicesPage };
        },
      },
      {
        path: "admin/announcements",
        lazy: async () => {
          const { AdminAnnouncementsPage } = await import("./components/admin/AdminAnnouncementsPage");
          return { Component: AdminAnnouncementsPage };
        },
      },
      {
        path: "admin/temizim-basvurulari",
        lazy: async () => {
          const { AdminTemizimApplicationsPage } = await import("./components/admin/AdminTemizimApplicationsPage");
          return { Component: AdminTemizimApplicationsPage };
        },
      },
      {
        path: "admin/migration",
        lazy: async () => {
          const { AdminMigrationPage } = await import("./components/admin/AdminMigrationPage");
          return { Component: AdminMigrationPage };
        },
      },
      {
        path: "admin/tickets",
        lazy: async () => {
          const { AdminTicketsPage } = await import("./components/admin/AdminTicketsPage");
          return { Component: AdminTicketsPage };
        },
      },
      {
        path: "admin/coupons",
        lazy: async () => {
          const { AdminCouponsPage } = await import("./components/admin/AdminCouponsPage");
          return { Component: AdminCouponsPage };
        },
      },

      // Protected routes with MainLayout
      {
        Component: MainLayout,
        children: [
          {
            index: true,
            lazy: async () => {
              const { OrderCreatePage } = await import("./components/OrderCreatePage");
              return { Component: OrderCreatePage };
            },
          },
          {
            path: "dashboard",
            lazy: async () => {
              const { DashboardPage } = await import("./components/DashboardPage");
              return { Component: DashboardPage };
            },
          },
          {
            path: "order/new",
            lazy: async () => {
              const { OrderCreatePage } = await import("./components/OrderCreatePage");
              return { Component: OrderCreatePage };
            },
          },
          {
            path: "delivery-list",
            lazy: async () => {
              const { DeliveryListPage } = await import("./components/DeliveryListPage");
              return { Component: DeliveryListPage };
            },
          },
          {
            path: "delivery",
            lazy: async () => {
              const { DeliveryPage } = await import("./components/DeliveryPage");
              return { Component: DeliveryPage };
            },
          },
          {
            path: "driver",
            lazy: async () => {
              const { DriverDashboardPage } = await import("./components/DriverDashboardPage");
              return { Component: DriverDashboardPage };
            },
          },
          {
            path: "driver/pickup",
            lazy: async () => {
              const { DriverPickupPage } = await import("./components/DriverPickupPage");
              // Şoför modülü ücretli paketlerde: ücretsizde "Satın al" penceresi + kilit kartı
              const { withPlanFeature } = await import("./components/PlanFeatureGate");
              return { Component: withPlanFeature('driver_module', DriverPickupPage) };
            },
          },
          {
            path: "receipt/new",
            lazy: async () => {
              const { ReceiptPrintPage } = await import("./components/ReceiptPrintPage");
              return { Component: ReceiptPrintPage };
            },
          },
          {
            path: "receipt/search",
            lazy: async () => {
              const { ReceiptPrintPage } = await import("./components/ReceiptPrintPage");
              return { Component: ReceiptPrintPage };
            },
          },
          {
            path: "receipt/:orderId",
            lazy: async () => {
              const { ReceiptPrintPage } = await import("./components/ReceiptPrintPage");
              return { Component: ReceiptPrintPage };
            },
          },
          {
            path: "customers",
            lazy: async () => {
              const { CustomersPage } = await import("./components/CustomersPage");
              return { Component: CustomersPage };
            },
          },
          {
            path: "customer/:id",
            lazy: async () => {
              const { CustomerAccountPage } = await import("./components/CustomerAccountPage");
              return { Component: CustomerAccountPage };
            },
          },
          {
            path: "customer-statement/:id",
            lazy: async () => {
              const { CustomerStatementPage } = await import("./components/CustomerStatementPage");
              return { Component: CustomerStatementPage };
            },
          },
          {
            path: "carpet-washing",
            lazy: async () => {
              const { CarpetWashingPage } = await import("./components/CarpetWashingPage");
              return { Component: CarpetWashingPage };
            },
          },
          {
            path: "products",
            lazy: async () => {
              const { ProductsPage } = await import("./components/ProductsPage");
              return { Component: ProductsPage };
            },
          },
          {
            path: "pricing",
            lazy: async () => {
              const { PricingPage } = await import("./components/PricingPage");
              return { Component: PricingPage };
            },
          },
          {
            path: "product-ordering",
            lazy: async () => {
              const { ProductOrderingPage } = await import("./components/ProductOrderingPage");
              return { Component: ProductOrderingPage };
            },
          },
          {
            path: "attributes",
            lazy: async () => {
              const { ProductAttributesPage } = await import("./components/ProductAttributesPage");
              return { Component: ProductAttributesPage };
            },
          },
          {
            path: "users",
            lazy: async () => {
              const { UsersPage } = await import("./components/UsersPage");
              return { Component: UsersPage };
            },
          },
          {
            path: "branches",
            lazy: async () => {
              const { BranchesPage } = await import("./components/BranchesPage");
              return { Component: BranchesPage };
            },
          },
          {
            path: "incoming-requests",
            lazy: async () => {
              const { IncomingRequestsPage } = await import("./components/IncomingRequestsPage");
              return { Component: IncomingRequestsPage };
            },
          },
          {
            path: "settings",
            lazy: async () => {
              const { SettingsPage } = await import("./components/SettingsPage");
              return { Component: SettingsPage };
            },
          },
          {
            path: "hardware",
            lazy: async () => {
              const { HardwarePage } = await import("./components/HardwarePage");
              return { Component: HardwarePage };
            },
          },
          {
            path: "profile",
            lazy: async () => {
              const { ProfilePage } = await import("./components/ProfilePage");
              return { Component: ProfilePage };
            },
          },
          {
            path: "subscription",
            lazy: async () => {
              const { SubscriptionPage } = await import("./components/SubscriptionPage");
              return { Component: SubscriptionPage };
            },
          },
          {
            path: "referral",
            lazy: async () => {
              const { ReferralPage } = await import("./components/ReferralPage");
              return { Component: ReferralPage };
            },
          },
          {
            path: "payment",
            lazy: async () => {
              const { PaymentPage } = await import("./components/PaymentPage");
              return { Component: PaymentPage };
            },
          },
          {
            path: "permissions",
            lazy: async () => {
              const { PermissionsPage } = await import("./components/PermissionsPage");
              return { Component: PermissionsPage };
            },
          },
          {
            path: "expenses",
            lazy: async () => {
              const { ExpensesPage } = await import("./components/ExpensesPage");
              return { Component: ExpensesPage };
            },
          },
          {
            path: "carwash",
            lazy: async () => {
              const { CarWashPage } = await import("./components/CarWashPage");
              return { Component: CarWashPage };
            },
          },
          {
            path: "barber",
            lazy: async () => {
              const { BarberPage } = await import("./components/BarberPage");
              return { Component: BarberPage };
            },
          },
          {
            path: "sofawash",
            lazy: async () => {
              const { SofaWashPage } = await import("./components/SofaWashPage");
              return { Component: SofaWashPage };
            },
          },
          {
            path: "z-report",
            lazy: async () => {
              const { ZReportPage } = await import("./components/ZReportPage");
              return { Component: ZReportPage };
            },
          },
          {
            path: "reports-list",
            lazy: async () => {
              const { ReportsListPage } = await import("./components/ReportsListPage");
              return { Component: ReportsListPage };
            },
          },
          {
            path: "reports-charts",
            lazy: async () => {
              const { ReportsChartsPage } = await import("./components/ReportsChartsPage");
              return { Component: ReportsChartsPage };
            },
          },
          {
            path: "account-statement",
            lazy: async () => {
              const { AccountStatementPage } = await import("./components/AccountStatementPage");
              return { Component: AccountStatementPage };
            },
          },
          {
            path: "invoices",
            lazy: async () => {
              const { InvoicesPage } = await import("./components/InvoicesPage");
              return { Component: InvoicesPage };
            },
          },
          {
            path: "tailoring",
            lazy: async () => {
              const { TailoringOptionsPage } = await import("./components/TailoringOptionsPage");
              return { Component: TailoringOptionsPage };
            },
          },
          {
            path: "notification-settings",
            lazy: async () => {
              const { NotificationSettingsPage } = await import("./components/NotificationSettingsPage");
              return { Component: NotificationSettingsPage };
            },
          },
          {
            path: "whatsapp-connection",
            lazy: async () => {
              const { WhatsAppConnectionPage } = await import("./components/WhatsAppConnectionPage");
              return { Component: WhatsAppConnectionPage };
            },
          },
          {
            path: "sms-connection",
            lazy: async () => {
              const { SMSConnectionPage } = await import("./components/SMSConnectionPage");
              return { Component: SMSConnectionPage };
            },
          },
          {
            path: "templates",
            lazy: async () => {
              const { TemplatesPage } = await import("./components/TemplatesPage");
              return { Component: TemplatesPage };
            },
          },
          {
            path: "language-test",
            lazy: async () => {
              const { LanguageTestPage } = await import("./components/LanguageTestPage");
              return { Component: LanguageTestPage };
            },
          },
          {
            path: "whats-new",
            lazy: async () => {
              const { WhatsNewPage } = await import("./components/WhatsNewPage");
              return { Component: WhatsNewPage };
            },
          },
          {
            path: "tickets",
            lazy: async () => {
              const m = await import("./components/TicketsPage");
              return { Component: m.default };
            },
          },
          {
            path: "tickets/:id",
            lazy: async () => {
              const m = await import("./components/TicketDetailPage");
              return { Component: m.default };
            },
          },
          {
            path: "customer-import",
            lazy: async () => {
              const { CustomerImportPage } = await import("./components/CustomerImportPage");
              return { Component: CustomerImportPage };
            },
          },
          {
            path: "whatsapp-test",
            lazy: async () => {
              const { WhatsAppTestPage } = await import("./components/WhatsAppTestPage");
              return { Component: WhatsAppTestPage };
            },
          },
          {
            path: "whatsapp-endpoint-test",
            lazy: async () => {
              const { WhatsAppEndpointTestPage } = await import("./components/WhatsAppEndpointTestPage");
              return { Component: WhatsAppEndpointTestPage };
            },
          },
        ],
      },

      // 404
      { path: "*", Component: NotFoundPage },
    ],
  },
]);
